import { DestroyRef, Injectable, Signal, signal } from '@angular/core';
import { environment } from '../../../environments/environment';

/**
 * Non-production helper that reveals, in each sheet's "Error Description"
 * column, whether an N8N extracted *baseline* is present for a calculated
 * monetary total — i.e. whether the "does not match the extracted sum"
 * advisory is even able to fire for that row (see
 * `ExtractedSumWarningService.baselineDebugForControl`).
 *
 * Bound to a single chord (Ctrl+Alt+B, chosen to dodge browser/OS defaults
 * and the existing Ctrl+Alt+{D,T,1,2,E} debug chords):
 *
 *   - `Ctrl+Alt+B` — toggle the baseline annotation on/off.
 *
 * ─── Production guard ──────────────────────────────────────────────────
 *
 * `bindGlobalShortcut` short-circuits on `environment.production` (a
 * compile-time const, so the listener body is dead-code-eliminated by the
 * optimiser). The signal therefore stays `false` in production and the
 * consuming sheets render no annotation. `ExtractedSumWarningService`
 * additionally early-returns in production as defence-in-depth.
 *
 * Pure UI state — no FormControls, no DOM mutation beyond the keydown
 * listener — so there is no transition-spinner machinery.
 */
@Injectable({ providedIn: 'root' })
export class ExtractedSumBaselineDebugService {
  private readonly _enabled = signal<boolean>(false);

  /** Whether the baseline annotation is currently shown. Templates/sheets branch on this. */
  readonly enabled: Signal<boolean> = this._enabled.asReadonly();

  private readonly handler = (event: KeyboardEvent) => this.onKey(event);
  private refCount = 0;

  bindGlobalShortcut(destroyRef: DestroyRef): void {
    if (environment.production) return;

    if (this.refCount === 0) {
      window.addEventListener('keydown', this.handler);
    }
    this.refCount++;

    destroyRef.onDestroy(() => {
      this.refCount = Math.max(0, this.refCount - 1);
      if (this.refCount === 0) {
        window.removeEventListener('keydown', this.handler);
      }
    });
  }

  private onKey(event: KeyboardEvent): void {
    if (!event.ctrlKey || !event.altKey || event.shiftKey || event.metaKey) return;
    if (event.code !== 'KeyB') return;
    event.preventDefault();
    this._enabled.update((on) => !on);
  }
}
